import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Clock, Mail, Flame } from "lucide-react";
import { company } from "../mock";
import { Button } from "./ui/button";

const SCROLL_THRESHOLD = 20;

const navItems = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > SCROLL_THRESHOLD);
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top info bar */}
      <div className="hidden md:block bg-zinc-950 border-b border-zinc-800/60 text-zinc-400 text-[13px]">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-10">
          <div className="flex items-center gap-6">
            <a href={`tel:${company.phone}`} className="flex items-center gap-2 hover:text-orange-400 transition-colors">
              <Phone className="w-3.5 h-3.5 text-orange-500" /> {company.phone}
            </a>
            <span className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-orange-500" /> {company.hours}
            </span>
          </div>
          <a href={`mailto:${company.email}`} className="flex items-center gap-2 hover:text-orange-400 transition-colors">
            <Mail className="w-3.5 h-3.5 text-orange-500" /> {company.email}
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div
        className={`transition-all duration-300 ${
          scrolled
            ? "bg-zinc-950/95 backdrop-blur-md border-b border-zinc-800/60 shadow-lg shadow-black/30"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-[72px]">
          <Link to="/" className="flex items-center gap-2.5 group">
            <span className="flex items-center justify-center w-10 h-10 rounded-md bg-gradient-to-br from-orange-500 to-amber-400">
              <Flame className="w-5 h-5 text-zinc-950" fill="currentColor" />
            </span>
            <span className="leading-tight">
              <span className="block font-display font-extrabold text-white text-lg tracking-tight">PHOENIX</span>
              <span className="block text-[10px] tracking-[0.2em] text-orange-400 font-medium -mt-0.5">DECORATIVE SURFACES</span>
              <span className="block text-[9px] tracking-[0.15em] text-zinc-400 font-medium">WESTERN NEW YORK DIVISION</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive ? "text-orange-400" : "text-zinc-300 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link to="/contact">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white font-semibold">Get a Quote</Button>
            </Link>
          </div>

          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-zinc-950/98 backdrop-blur-md border-b border-zinc-800">
          <nav className="flex flex-col px-6 py-4 gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-md text-base font-medium ${
                    isActive ? "text-orange-400 bg-zinc-900" : "text-zinc-200"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/contact" className="mt-2">
              <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold">Get a Quote</Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
